import * as React from 'react';
import { PopupButton, PopupContent, usePopup } from '@dx-ui/osc-popup';
import { sendInteractionReward, SpeechBubbleContext } from '../header.utilities';
import { Dialog } from '@dx-ui/osc-dialog-v2';
import { useTranslation } from 'next-i18next';
import { LoginForm } from '@dx-ui/osc-login';
import { Link } from '@dx-ui/osc-link';
import { JoinModal } from '@dx-ui/osc-join-modal-button';
import cx from 'classnames';
import Icon, { type IconName } from '@dx-ui/osc-icon';
import { Surface, Surfaces } from '@dx-ui/global-ui-prism';
import type { LoginBaseProps, LoginFormProps, LoginFormResponse } from '@dx-ui/osc-login';
import { sendReward } from '@dx-ui/framework-conductrics';
import { SpeechBubble } from './speech-bubble';
import type { HeaderAnalytics, NavItemProps, NavItemSection } from '../types';

export function NavItem({ children, isOpen = false, className, ...listItemProps }: NavItemProps) {
  const spacedChild = React.cloneElement(children, {
    className: cx(children.props.className, 'flex-1 self-stretch'),
  });

  return (
    <li
      {...listItemProps}
      className={cx(
        'osc-nav-list-item text--sm group flex h-full items-center border-t-4 border-t-transparent px-1',
        className,
        {
          'osc-nav-list-item-active': isOpen,
        }
      )}
    >
      {spacedChild}
    </li>
  );
}

export function NavItemVisual({
  label,
  icon,
  indicator,
  hasRotatedIndicator,
  section,
}: {
  label: string;
  icon?: IconName;
  indicator?: IconName;
  section: NavItemSection;
  hasRotatedIndicator?: boolean;
}) {
  return (
    <>
      <span className={cx('block min-h-5', section === 'user' ? 'osc-nav-item-text' : null)}>
        {label}
      </span>
      {icon ? (
        <span className="ps-2">
          <Icon
            name={icon}
            className="group-focus-within:hidden group-hover:hidden"
            wrapper="span"
          />
          <Icon
            name={icon}
            variant="solid"
            className={cx(
              'osc-nav-icon-active',
              'hidden group-focus-within:block group-hover:block'
            )}
            wrapper="span"
          />
        </span>
      ) : null}

      {indicator ? (
        <span>
          <Icon
            name={indicator}
            className={cx(
              hasRotatedIndicator ? 'rotate-180' : null,
              'group-focus-within:hidden group-hover:hidden'
            )}
            wrapper="span"
          />
          <Icon
            name={indicator}
            className={cx(
              hasRotatedIndicator ? 'rotate-180' : null,
              'hidden group-focus-within:block group-hover:block'
            )}
            wrapper="span"
          />
        </span>
      ) : null}
    </>
  );
}

type NavItemWithLinkProps = Omit<Link, 'className' | 'children' | 'icon'> & {
  section: NavItemSection;
  label: string;
  icon?: React.ReactElement;
  namedIcon?: IconName;
  isWide?: boolean;
  experimentationConfiguration?: CmsExperimentationConfiguration;
  navItemClassName?: string;
};

//TODO: make the focus ring here take up the full box, like with the dropdowns. Also check ItemWithModal
export const NavItemWithLink: React.FC<NavItemWithLinkProps> = ({
  namedIcon,
  section,
  label,
  isWide,
  experimentationConfiguration,
  navItemClassName,
  ...rest
}) => {
  return (
    <NavItem data-testid="with-link" className={navItemClassName}>
      <Link
        underline={false}
        {...rest}
        data-conductrics-goal={experimentationConfiguration?.goal}
        data-conductrics-value={experimentationConfiguration?.value}
        anchorClassName="h-full flex"
        className={cx('flex items-center whitespace-nowrap', isWide ? 'p-2' : 'p-1', {
          'osc-nav-main-link': section === 'navigation',
        })}
      >
        <NavItemVisual section={section} label={label} icon={namedIcon} />
      </Link>
    </NavItem>
  );
};

export type TNavItemWithModal = {
  /** callback to run after login attempt */
  onLoginAttempt: React.ComponentProps<typeof LoginForm>['onLoginAttempt'];
  /** The trigger for the dialog. Replaces desktop nav button */
  dialogTrigger?: React.ReactNode;
  /** aria-label of the button */
  buttonLabel?: string;
  buttonIcon?: IconName;
  onClick?: () => void;
  section: NavItemSection;
  navItemClassName?: string;
  shouldOpenJoinInPage?: boolean;
  locale?: string;
  loggedInButtonRef?: React.RefObject<HTMLButtonElement | null>;
  analytics?: HeaderAnalytics;
};

export const NavItemWithModal: React.FC<React.PropsWithChildren<TNavItemWithModal>> = ({
  section,
  buttonLabel,
  buttonIcon,
  onClick,
  onLoginAttempt,
  analytics,
  children,
  navItemClassName,
  shouldOpenJoinInPage,
  locale = 'en',
  dialogTrigger,
  loggedInButtonRef,
}) => {
  const [showDialog, setShowDialog] = React.useState<boolean>(false);
  const [showJoinModal, setShowJoinModal] = React.useState(false);
  const open = (open: boolean) => {
    if (open) {
      sendInteractionReward();
      onClick?.();
      setShowDialog(true);
    }
  };
  const close = () => {
    setShowDialog(false);
  };

  const handleLogin = (response: LoginFormResponse) => {
    onLoginAttempt?.(response);
    if (!response.error) {
      sendReward('g-IqzR2S0Ai5');
      close();
    }
  };

  const onJoinClick = () => {
    setShowJoinModal(true);
    close();
  };

  const arrayChildren = React.Children.toArray(children);

  const [t] = useTranslation('osc-login');

  const signInButtonRef = React.useRef<React.ComponentRef<'button'>>(null);

  return (
    <>
      {!shouldOpenJoinInPage ? (
        <JoinModal
          analytics={analytics}
          isOpen={showJoinModal}
          language={locale}
          onLoginAttempt={onLoginAttempt}
          onDismiss={() => {
            setShowJoinModal(false);
            setTimeout(() => signInButtonRef.current?.focus(), 100);
          }}
          onSuccessfulLogin={() => {
            setShowJoinModal(false);
            setTimeout(() => loggedInButtonRef?.current?.focus(), 1000);
          }}
        />
      ) : null}
      {!dialogTrigger ? (
        <NavItem data-testid="with-modal" className={navItemClassName}>
          <button
            type="button"
            className="text--sm group flex h-full items-center whitespace-nowrap px-1"
            data-testid="with-modal"
            ref={signInButtonRef}
            onClick={() => open(true)}
          >
            <NavItemVisual label={buttonLabel ?? t('signIn')} icon={buttonIcon} section={section} />
          </button>
        </NavItem>
      ) : null}
      <Dialog
        isOpen={showDialog && !showJoinModal}
        onDismiss={close}
        size="md"
        title={t('signIn')}
        onOpenChange={open}
        dialogTrigger={dialogTrigger}
      >
        {React.Children.map(arrayChildren, (child) => {
          if (React.isValidElement(child) && child.type === LoginForm) {
            const loginFormElement = child as React.ReactElement<
              React.ComponentProps<typeof LoginForm>
            >;
            const loginFormConfig = !shouldOpenJoinInPage
              ? {
                  ...(loginFormElement.props.loginFormConfig ?? {
                    showCreatePasswordLink: false,
                    showForgetInfoLink: true,
                    showJoinLink: true,
                    showSecureInfoLink: false,
                  }),
                  onJoinClick,
                  joinFromModal: true,
                }
              : loginFormElement.props.loginFormConfig;

            return React.cloneElement(loginFormElement, {
              onLoginAttempt: handleLogin,
              isInModal: true,
              loginFormConfig,
              analytics: analytics ?? loginFormElement.props.analytics,
              isLegacySignInAnalyticsEnabled:
                analytics?.isLegacySignInAnalyticsEnabled ??
                loginFormElement.props.isLegacySignInAnalyticsEnabled,
            });
          }
          return child;
        })}
      </Dialog>
    </>
  );
};

type TNavItemWithPopup = {
  section: NavItemSection;
  onClick?: () => void;
  popupButtonRef?: React.RefObject<HTMLButtonElement | null>;
  label: string;
  icon?: IconName;
  indicator?: IconName;
  rotateOnOpen?: boolean;
  experimentationConfiguration?: CmsExperimentationConfiguration;
  surfaceType?: Surfaces;
  isTranslucent?: boolean;
};

export const NavItemWithPopup: React.FC<React.PropsWithChildren<TNavItemWithPopup>> = ({
  children,
  label,
  icon,
  rotateOnOpen,
  onClick,
  popupButtonRef,
  section,
  indicator,
  experimentationConfiguration,
  surfaceType,
  isTranslucent,
}) => {
  const { isOpen } = usePopup();

  return (
    <>
      <NavItem isOpen={isOpen} data-testid="with-popup">
        <PopupButton
          ref={popupButtonRef}
          data-conductrics-goal={experimentationConfiguration?.goal}
          data-conductrics-value={experimentationConfiguration?.value}
          onClick={() => {
            sendInteractionReward();
            onClick?.();
          }}
          className={cx('flex items-center whitespace-nowrap p-2', {
            'osc-nav-main-link': section === 'navigation',
          })}
        >
          <NavItemVisual
            label={label}
            section={section}
            indicator={indicator}
            icon={icon}
            hasRotatedIndicator={!isOpen && rotateOnOpen}
          />
        </PopupButton>
      </NavItem>
      <li>
        <PopupContent
          className={cx('!osc-popup-margin !w-screen !p-0 motion-safe:animate-fadein', {
            '!bg-transparent': isTranslucent,
          })}
          popupStyles={{ left: 0 }}
        >
          <Surface
            type={surfaceType || 'base'}
            className={cx('osc-nav-drawer', { '!bg-translucent bg-blur': isTranslucent })}
          >
            {children}
          </Surface>
        </PopupContent>
      </li>
    </>
  );
};

type NavItemWithSpeechBubbleProps = {
  onLoginAttempt: React.ComponentProps<typeof LoginForm>['onLoginAttempt'];
  options?: LoginFormProps['loginFormConfig'];
  buttonClassName: LoginBaseProps['buttonClassName'];
  onClick?: () => void;
  onDialogOpen?: () => void;
  shouldOpenJoinInPage?: boolean;
  locale?: string;
  loggedInButtonRef?: React.RefObject<HTMLButtonElement | null>;
  analytics?: HeaderAnalytics;
};

export const NavItemWithSpeechBubble = ({
  onLoginAttempt,
  analytics,
  options,
  buttonClassName,
  onClick,
  onDialogOpen,
  children,
  shouldOpenJoinInPage,
  locale = 'en',
  loggedInButtonRef,
}: React.PropsWithChildren<NavItemWithSpeechBubbleProps>) => {
  const { onDismiss } = React.useContext(SpeechBubbleContext);
  const [showDialog, setShowDialog] = React.useState(false);
  const [showSpeechBubble, setShowSpeechBubble] = React.useState(true);
  const [t] = useTranslation('osc-login');
  const [showJoinModal, setShowJoinModal] = React.useState(false);
  const [speechBubbleContainer, setSpeechBubbleContainer] = React.useState<
    HTMLDivElement | undefined
  >(undefined);

  const buttonRef = React.useRef<React.ComponentRef<'button'>>(null);
  const wasDialogOpenRef = React.useRef(false);

  React.useEffect(() => {
    if (showDialog) {
      wasDialogOpenRef.current = true;
    } else if (wasDialogOpenRef.current && !showJoinModal) {
      wasDialogOpenRef.current = false;
      onDialogOpen?.();
    }
  }, [showDialog, showJoinModal, onDialogOpen]);

  const onSignInClick = () => {
    onClick?.();
    setShowDialog(true);
    setShowSpeechBubble(false);
    sendInteractionReward();
    sendReward('nav-sign-in-click');
  };

  const close = () => setShowDialog(false);

  const closeSpeechBubble = () => {
    setShowSpeechBubble(false);
    buttonRef?.current?.focus();
  };
  const handleSpeechBubbleDismiss = () => {
    onDismiss?.();
    closeSpeechBubble();
  };
  const setRef = (node: HTMLDivElement) => {
    if (node) {
      setSpeechBubbleContainer(node);
    }
  };

  const handleLogin = (response: LoginFormResponse) => {
    onLoginAttempt?.(response);
    if (!response.error) {
      sendReward('g-IqzR2S0Ai5');
      close();
    }
  };

  const onJoinClick = () => {
    setShowJoinModal(true);
    close();
  };

  const loginFormConfig = !shouldOpenJoinInPage
    ? {
        ...(options ?? {
          showCreatePasswordLink: false,
          showForgetInfoLink: true,
          showJoinLink: true,
          showSecureInfoLink: false,
        }),
        onJoinClick,
        joinFromModal: true,
      }
    : options;
  return (
    <>
      {!shouldOpenJoinInPage ? (
        <JoinModal
          analytics={analytics}
          isOpen={showJoinModal && !showDialog}
          language={locale}
          onLoginAttempt={onLoginAttempt}
          onDismiss={() => {
            setShowJoinModal(false);
            setTimeout(() => buttonRef.current?.focus(), 100);
          }}
          onSuccessfulLogin={() => {
            setShowJoinModal(false);
            setTimeout(() => loggedInButtonRef?.current?.focus(), 1000);
          }}
        />
      ) : null}
      <SpeechBubble
        closeSpeechBubble={handleSpeechBubbleDismiss}
        showSpeechBubble={showSpeechBubble}
        containerRef={speechBubbleContainer}
        onInlineButtonClick={() => {
          closeSpeechBubble?.();
          setShowDialog(true);
        }}
      >
        <button
          data-testid="with-modal"
          type="button"
          className={buttonClassName}
          onClick={onSignInClick}
          ref={buttonRef}
          aria-expanded={undefined}
          aria-describedby="sign-in-note"
        >
          {children}
        </button>
      </SpeechBubble>
      <Dialog title={t('signIn')} isOpen={showDialog && !showJoinModal} onDismiss={close} size="md">
        <LoginForm
          loginFormConfig={loginFormConfig}
          onLoginAttempt={handleLogin}
          analytics={analytics}
          isLegacySignInAnalyticsEnabled={analytics?.isLegacySignInAnalyticsEnabled}
          sourceId="OHW"
          isInModal={true}
        />
      </Dialog>
      <div ref={setRef} />
    </>
  );
};
